import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Input,
  Badge,
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { Info, Loader2, RefreshCw, Search, Undo2 } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import {
  getInsuranceVisitRateDefaults,
  getInsuranceVisitRates,
  replaceInsuranceVisitRates,
} from '../../services/clinicsApi';
import type {
  ChargeType,
  InsuranceVisitRateManualRecord,
  InsuranceVisitRateResponse,
  InsuranceVisitRateVisitType,
  RateSource,
  VisitTypeId,
} from '../../services/api/types.gen';
import { useStore } from '../../store';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { AlsoCalled } from './AlsoCalled';
import { matchesNameOrAlias } from './matchesNameOrAlias';
import { disciplineName, groupByDiscipline } from '../../constants/disciplines';
import {
  CHARGE_TYPE_LABELS,
  CLAIM_UNITS_BASIS_OPTIONS,
  RATE_CHARGE_BASIS_OPTIONS,
} from '../../constants/insuranceBilling';

type InsuranceRatesPanelProps = {
  insuranceId: string;
  // When true, this insurance is synced from WellSky: rates are still editable,
  // but a re-sync may overwrite manual changes, so we surface a quiet notice.
  wellSkySynced?: boolean;
  // The insurance-level charge type that a row-level basis left on "Inherit"
  // falls back to; shown in the Inherit option so the effective value is
  // visible without leaving the grid.
  inheritedChargeType?: ChargeType | null;
  // Unsaved-edit state, reported so the owning modal can confirm before
  // closing rather than silently dropping rate edits.
  onDirtyChange?: (dirty: boolean) => void;
  // Persisted rate-row count, reported after each load/save so the owning
  // tab label can carry it.
  onRatesCountChange?: (count: number) => void;
};

// Sentinel for the "inherit payer default" option, since Radix Select forbids
// an empty-string item value. Maps to `null` in the saved record.
const INHERIT = '__inherit__';

const MAX_MODIFIERS = 4;

// Editable state for one visit-type row. Strings mirror the raw input values so
// an in-progress edit (e.g. a lone ".") round-trips without coercion surprises.
type RowState = {
  hcpcsCode: string;
  revenueCode: string;
  // Preserved but not surfaced in the grid — carrying it through save avoids
  // silently dropping a synced row's service code on a replace-all.
  serviceCode: string;
  modifiersText: string;
  allowedAmount: string;
  chargeBy: ChargeType | '';
  allowedBy: ChargeType | '';
  claimUnitsBasis: ChargeType | '';
  claimUnitsConstant: string;
  active: boolean;
};

const EMPTY_ROW: RowState = {
  hcpcsCode: '',
  revenueCode: '',
  serviceCode: '',
  modifiersText: '',
  allowedAmount: '',
  chargeBy: '',
  allowedBy: '',
  claimUnitsBasis: '',
  claimUnitsConstant: '',
  active: true,
};

const seedRow = (r: InsuranceVisitRateResponse): RowState => ({
  hcpcsCode: r.hcpcs_code ?? '',
  revenueCode: r.revenue_code ?? '',
  serviceCode: r.service_code ?? '',
  modifiersText: (r.modifiers ?? []).join(' '),
  allowedAmount: r.allowed_amount ?? '',
  chargeBy: r.charge_by ?? '',
  allowedBy: r.allowed_by ?? '',
  claimUnitsBasis: r.claim_units_basis ?? '',
  claimUnitsConstant:
    r.claim_units_constant != null ? String(r.claim_units_constant) : '',
  active: r.active,
});

const parseModifiers = (text: string): string[] =>
  text
    .split(/[\s,]+/)
    .map(m => m.trim())
    .filter(Boolean);

// A row carries meaning if the admin set any code, amount, basis, modifier, or
// explicitly deactivated it. Meaningless rows are omitted from the replace-all
// payload (backend deletes anything not sent), so blank rows never persist.
const isRowMeaningful = (row: RowState): boolean =>
  row.hcpcsCode.trim() !== '' ||
  row.revenueCode.trim() !== '' ||
  row.serviceCode.trim() !== '' ||
  row.allowedAmount.trim() !== '' ||
  row.chargeBy !== '' ||
  row.allowedBy !== '' ||
  row.claimUnitsBasis !== '' ||
  parseModifiers(row.modifiersText).length > 0 ||
  row.active === false;

const buildRecord = (
  visitTypeId: VisitTypeId,
  row: RowState
): InsuranceVisitRateManualRecord => {
  const isConstant = row.claimUnitsBasis === 'CONSTANT';
  return {
    visit_type_id: visitTypeId,
    hcpcs_code: row.hcpcsCode.trim() || null,
    revenue_code: row.revenueCode.trim() || null,
    service_code: row.serviceCode.trim() || null,
    modifiers: parseModifiers(row.modifiersText),
    allowed_amount: row.allowedAmount.trim() || null,
    charge_by: row.chargeBy || null,
    allowed_by: row.allowedBy || null,
    claim_units_basis: row.claimUnitsBasis || null,
    // Constant only rides along with a CONSTANT basis — the backend 422s
    // otherwise, so we never send it in any other case.
    claim_units_constant:
      isConstant && row.claimUnitsConstant.trim() !== ''
        ? Number(row.claimUnitsConstant)
        : null,
    active: row.active,
  };
};

// The fields a defaults reset proposes. Everything else has no default source
// and is never touched by a proposal.
const RESET_FIELDS = ['hcpcsCode', 'revenueCode'] as const;
type ResetField = (typeof RESET_FIELDS)[number];

// Proposal-relevant cells that differ from the pre-reset snapshot, by visit
// type. Only visit types with at least one changed field appear as keys.
const changedResetCells = (
  rows: Record<string, RowState>,
  preResetRows: Record<string, RowState>
): Map<string, ResetField[]> => {
  const changed = new Map<string, ResetField[]>();
  for (const [visitTypeId, before] of Object.entries(preResetRows)) {
    const after = rows[visitTypeId];
    if (!after) continue;
    const fields = RESET_FIELDS.filter(field => after[field] !== before[field]);
    if (fields.length > 0) changed.set(visitTypeId, fields);
  }
  return changed;
};

const countChangedCells = (changed: Map<string, ResetField[]>): number =>
  [...changed.values()].reduce((count, fields) => count + fields.length, 0);

// Stable serialization of the meaningful payload, used for dirty tracking.
const snapshotRows = (rows: Record<string, RowState>): string => {
  const records = Object.keys(rows)
    .sort()
    .filter(id => isRowMeaningful(rows[id]))
    .map(id => buildRecord(id as VisitTypeId, rows[id]));
  return JSON.stringify(records);
};

type DisciplineGroup = {
  discipline: string;
  label: string;
  visitTypes: InsuranceVisitRateVisitType[];
};

const groupVisitTypesByDiscipline = (
  visitTypes: InsuranceVisitRateVisitType[]
): DisciplineGroup[] =>
  groupByDiscipline(visitTypes, vt => vt.discipline).map(
    ({ discipline, items }) => ({
      discipline,
      label: disciplineName(discipline),
      visitTypes: items,
    })
  );

/**
 * Per-insurance visit rates grid, hosted by the Insurance modal's
 * "Billing rates" tab. Owns loading, editing, and saving; the owning modal
 * handles close/discard via onDirtyChange.
 */
const InsuranceRatesPanel: React.FC<InsuranceRatesPanelProps> = ({
  insuranceId,
  wellSkySynced = false,
  inheritedChargeType = null,
  onDirtyChange,
  onRatesCountChange,
}) => {
  const { showSuccess, showError } = useStore(
    useShallow(state => ({
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDefaults, setIsLoadingDefaults] = useState(false);
  // Save is gated on this: it only goes true once THIS insurance's rates have
  // loaded, so a failed load can never leave a previous insurance's rows
  // editable and silently saveable under the wrong insurance id.
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [visitTypes, setVisitTypes] = useState<InsuranceVisitRateVisitType[]>(
    []
  );
  const [rows, setRows] = useState<Record<string, RowState>>({});
  // Persisted provenance per visit type; DEFAULT rows carry the seeded-value
  // treatment until a human reviews them.
  const [sourceByVisitType, setSourceByVisitType] = useState<
    Record<string, RateSource>
  >({});
  // Non-null while a defaults review is in progress: the working rows as they
  // were the moment the proposal was applied. Discard restores this exactly.
  const [preResetRows, setPreResetRows] = useState<Record<
    string,
    RowState
  > | null>(null);
  const [initialSnapshot, setInitialSnapshot] = useState('[]');
  const [openDisciplines, setOpenDisciplines] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const inResetReview = preResetRows !== null;

  // Identifies the currently loaded grid; bumped by every loadRates. Async
  // handlers capture it before awaiting and bail if it moved — a defaults
  // response for insurance A must never land on insurance B's grid.
  const gridSessionRef = useRef(0);
  // Latest committed rows, so async handlers snapshot the current grid rather
  // than the render they were created in (keystrokes during an in-flight
  // fetch would otherwise be lost).
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const seedFromResponse = (
    fetchedVisitTypes: InsuranceVisitRateVisitType[],
    rates: InsuranceVisitRateResponse[]
  ) => {
    const rateByVisitType = new Map(rates.map(r => [r.visit_type_id, r]));
    const nextRows: Record<string, RowState> = {};
    const nextSources: Record<string, RateSource> = {};
    for (const vt of fetchedVisitTypes) {
      const rate = rateByVisitType.get(vt.visit_type_id);
      nextRows[vt.visit_type_id] = rate ? seedRow(rate) : { ...EMPTY_ROW };
      if (rate) nextSources[vt.visit_type_id] = rate.source;
    }
    setVisitTypes(fetchedVisitTypes);
    setRows(nextRows);
    setSourceByVisitType(nextSources);
    // Any authoritative reload ends an in-flight defaults review.
    setPreResetRows(null);
    setInitialSnapshot(snapshotRows(nextRows));
    onRatesCountChange?.(rates.length);

    // Open the first discipline that already has configured rates, else the
    // first group (Skilled Nursing when present).
    const groups = groupVisitTypesByDiscipline(fetchedVisitTypes);
    const firstWithData = groups.find(g =>
      g.visitTypes.some(vt => isRowMeaningful(nextRows[vt.visit_type_id]))
    );
    const openTo = firstWithData ?? groups[0];
    setOpenDisciplines(openTo ? [openTo.discipline] : []);
  };

  const loadRates = async () => {
    // Any in-flight defaults fetch belongs to the previous grid session; the
    // bump makes its late response a no-op instead of a cross-insurance write.
    const session = gridSessionRef.current + 1;
    gridSessionRef.current = session;
    setIsLoading(true);
    // Clear any previously loaded grid up front so a failed load shows the
    // error state below instead of leaving stale (possibly another
    // insurance's) rows on screen. Mode state clears with it — a defaults
    // review must never survive into another insurance's grid.
    setHasLoaded(false);
    setVisitTypes([]);
    setRows({});
    setSourceByVisitType({});
    setPreResetRows(null);
    setInitialSnapshot('[]');
    try {
      const { data } = await getInsuranceVisitRates({
        path: { insurance_id: insuranceId },
        throwOnError: true,
      });
      // The grid reloaded (or switched insurance) while we were fetching:
      // this response belongs to a session that no longer exists.
      if (session !== gridSessionRef.current) return;
      seedFromResponse(data.visit_types, data.rates);
      setHasLoaded(true);
    } catch (error) {
      console.error('Failed to load insurance rates:', error);
      showError(
        'Failed to load rates',
        getBusinessErrorMessage(error) ??
          'Unable to load data. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Load whenever the panel is mounted for an insurance (the modal mounts one
  // panel per insurance being edited).
  useEffect(() => {
    if (insuranceId) {
      void loadRates();
    }
  }, [insuranceId]);

  const updateRow = (visitTypeId: string, patch: Partial<RowState>) => {
    setRows(prev => ({
      ...prev,
      [visitTypeId]: { ...prev[visitTypeId], ...patch },
    }));
  };

  const groups = useMemo(
    () => groupVisitTypesByDiscipline(visitTypes),
    [visitTypes]
  );

  // Client-side filter on visit type name (or discipline label). While a
  // search is active every matching group is expanded so hits are visible.
  const query = searchQuery.trim().toLowerCase();
  const visibleGroups = useMemo(() => {
    if (!query) return groups;
    return groups
      .map(group => ({
        ...group,
        visitTypes: group.visitTypes.filter(
          vt =>
            matchesNameOrAlias(query, vt.display_name, vt.aliases) ||
            group.label.toLowerCase().includes(query)
        ),
      }))
      .filter(group => group.visitTypes.length > 0);
  }, [groups, query]);
  const accordionValue = query
    ? visibleGroups.map(g => g.discipline)
    : openDisciplines;

  const currentSnapshot = useMemo(() => snapshotRows(rows), [rows]);
  const isDirty = currentSnapshot !== initialSnapshot;

  // Keep the owning modal informed so it can confirm before discarding.
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // Counted over the whole grid, not the visible/filtered slice — collapsed
  // accordions and active searches must not hide proposed changes from the
  // review count.
  const resetChangedCells = useMemo(
    () =>
      preResetRows
        ? changedResetCells(rows, preResetRows)
        : new Map<string, ResetField[]>(),
    [rows, preResetRows]
  );
  const resetChangedCount = useMemo(
    () => countChangedCells(resetChangedCells),
    [resetChangedCells]
  );

  const changedCountForGroup = (group: DisciplineGroup): number =>
    group.visitTypes.reduce(
      (count, vt) =>
        count + (resetChangedCells.get(vt.visit_type_id)?.length ?? 0),
      0
    );
  const hasModifierError = useMemo(
    () =>
      Object.values(rows).some(
        row => parseModifiers(row.modifiersText).length > MAX_MODIFIERS
      ),
    [rows]
  );

  // Shared by the footer Save and the review-mode Accept. `markReviewed` tells
  // the backend an admin explicitly accepted a defaults review, so unchanged
  // DEFAULT rows shed their provisional status.
  const saveRates = async (markReviewed: boolean) => {
    if (hasModifierError) {
      showError(
        'Too many modifiers',
        `A visit type has more than ${MAX_MODIFIERS} modifiers. Remove the extras before saving.`
      );
      return;
    }
    const records = Object.keys(rows)
      .filter(id => isRowMeaningful(rows[id]))
      .map(id => buildRecord(id as VisitTypeId, rows[id]));

    setIsSaving(true);
    try {
      const { data } = await replaceInsuranceVisitRates({
        path: { insurance_id: insuranceId },
        body: { rates: records, mark_reviewed: markReviewed },
        throwOnError: true,
      });
      // Re-seed from the authoritative grid so the editor reflects exactly what
      // persisted (dirty state resets, and an active review mode exits).
      seedFromResponse(data.visit_types, data.rates);
      showSuccess(markReviewed ? 'Defaults accepted' : 'Rates saved');
    } catch (error) {
      // On failure everything stays as it was — including an in-flight
      // defaults review, so the admin can retry or discard.
      console.error('Failed to save insurance rates:', error);
      showError(
        'Failed to save rates',
        getBusinessErrorMessage(error) ?? 'Unable to save. Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = () => void saveRates(false);
  const handleAcceptReview = () => void saveRates(true);

  // Reset-to-defaults review (EMR-198): apply Roger's default codes as a
  // client-side proposal over the working grid. Nothing persists until Accept.
  const handleResetToDefaults = async () => {
    const session = gridSessionRef.current;
    setIsLoadingDefaults(true);
    try {
      const { data } = await getInsuranceVisitRateDefaults({
        path: { insurance_id: insuranceId },
        throwOnError: true,
      });
      // The grid reloaded (or switched insurance) while we were fetching:
      // this response belongs to a session that no longer exists.
      if (session !== gridSessionRef.current) return;
      const snapshot = rowsRef.current;
      const proposed: Record<string, RowState> = { ...snapshot };
      // Only visit types the defaults response names are touched, and a null
      // code means "no default exists" — the cell keeps its current value
      // rather than being proposed blank. (Legacy visit types no longer
      // reach the grid at all: the API excludes them since EMR-891.)
      for (const record of data.defaults) {
        const existing = proposed[record.visit_type_id];
        if (!existing) continue;
        proposed[record.visit_type_id] = {
          ...existing,
          hcpcsCode: record.hcpcs_code ?? existing.hcpcsCode,
          revenueCode: record.revenue_code ?? existing.revenueCode,
        };
      }
      const changed = changedResetCells(proposed, snapshot);
      // With no proposed change the mode only opens when Accept could do
      // something: unreviewed seeded rows exist AND the insurance is native.
      // On WellSky-managed insurances the backend deliberately keeps DEFAULT
      // rows machine-owned (they'd lose their stale-delete immunity), so a
      // zero-diff "mark reviewed" would be an unsatisfiable dead-end there.
      if (
        countChangedCells(changed) === 0 &&
        (!hasSeededRows || wellSkySynced)
      ) {
        showSuccess('Rates already match Roger defaults');
        return;
      }
      setPreResetRows(snapshot);
      setRows(proposed);
      // Every group with a proposed change opens so the review is actually
      // reviewable; the changed-count badges cover anything re-collapsed.
      const disciplinesWithChanges = groups
        .filter(group =>
          group.visitTypes.some(vt => changed.has(vt.visit_type_id))
        )
        .map(group => group.discipline);
      setOpenDisciplines(prev => [
        ...new Set([...prev, ...disciplinesWithChanges]),
      ]);
    } catch (error) {
      console.error('Failed to load default rates:', error);
      showError(
        'Failed to load defaults',
        getBusinessErrorMessage(error) ??
          'Unable to load defaults. Please try again.'
      );
    } finally {
      setIsLoadingDefaults(false);
    }
  };

  const discardReview = () => {
    if (preResetRows) setRows(preResetRows);
    setPreResetRows(null);
  };

  const revertResetCell = (visitTypeId: string, field: ResetField) => {
    const before = preResetRows?.[visitTypeId];
    if (!before) return;
    updateRow(visitTypeId, { [field]: before[field] });
  };

  const configuredCount = (group: DisciplineGroup): number =>
    group.visitTypes.filter(vt => isRowMeaningful(rows[vt.visit_type_id]))
      .length;

  const hasSeededRows = useMemo(
    () => Object.values(sourceByVisitType).some(source => source === 'DEFAULT'),
    [sourceByVisitType]
  );

  return (
    <div className='flex min-h-0 flex-1 flex-col'>
      {wellSkySynced && (
        <div className='mb-3 flex items-start gap-2 rounded-md border border-border bg-muted/50 p-3 text-xs text-muted-foreground'>
          <RefreshCw className='h-4 w-4 shrink-0 mt-0.5' />
          <span>
            These rates sync from WellSky. Manual changes may be overwritten on
            the next sync.
          </span>
        </div>
      )}
      {hasSeededRows && !inResetReview && (
        <div
          data-testid='seeded-defaults-banner'
          className='mb-3 flex items-start gap-2 rounded-md border border-seeded-border/40 bg-seeded p-3 text-xs text-seeded-foreground'
        >
          <Info className='h-4 w-4 shrink-0 mt-0.5' />
          <span>
            Roger filled in default billing codes. Review them for this payer.
          </span>
        </div>
      )}

      {inResetReview && (
        <div
          data-testid='reset-review-bar'
          className='mb-3 flex items-center gap-3 rounded-md border border-seeded-border/40 bg-seeded px-3 py-3'
        >
          <span className='text-sm font-medium text-seeded-foreground'>
            {resetChangedCount > 0
              ? `Reviewing default codes — ${resetChangedCount} changed`
              : 'Codes already match defaults — Accept to mark them reviewed'}
          </span>
          <span className='ml-auto flex items-center gap-2'>
            <Button
              variant='outline'
              size='sm'
              onClick={discardReview}
              disabled={isSaving}
            >
              Discard Edits
            </Button>
            <Button size='sm' onClick={handleAcceptReview} disabled={isSaving}>
              {isSaving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              Accept Edits
            </Button>
          </span>
        </div>
      )}

      <div className='min-h-0 flex-1 overflow-y-auto'>
        {isLoading ? (
          <div className='flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground'>
            <Loader2 className='h-5 w-5 animate-spin' />
            Loading rates…
          </div>
        ) : !hasLoaded ? (
          <div className='flex flex-col items-center gap-3 py-16 text-sm text-muted-foreground'>
            <span>Rates could not be loaded.</span>
            <Button variant='outline' onClick={() => void loadRates()}>
              <RefreshCw className='mr-2 h-4 w-4' />
              Try again
            </Button>
          </div>
        ) : (
          <>
            <div className='relative mb-3 max-w-xs'>
              <Search className='pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
              <Input
                inputSize='sm'
                className='pl-8'
                placeholder='Search visit types'
                aria-label='Search visit types'
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
            {visibleGroups.length === 0 ? (
              <p className='py-10 text-center text-sm text-muted-foreground'>
                No visit types match your search
              </p>
            ) : (
              <Accordion
                type='multiple'
                value={accordionValue}
                onValueChange={value => {
                  // Manual toggles only stick outside an active search;
                  // during one the expansion is derived from the matches.
                  if (!query) setOpenDisciplines(value);
                }}
              >
                {visibleGroups.map(group => (
                  <AccordionItem
                    key={group.discipline}
                    value={group.discipline}
                  >
                    <AccordionTrigger>
                      <span className='flex items-center gap-2'>
                        <span className='font-medium'>{group.label}</span>
                        <span className='text-xs font-normal text-muted-foreground'>
                          {configuredCount(group)} of {group.visitTypes.length}{' '}
                          set
                        </span>
                        {inResetReview && changedCountForGroup(group) > 0 && (
                          <Badge
                            variant='secondary'
                            className='bg-seeded-strong text-seeded-foreground'
                          >
                            {changedCountForGroup(group)} proposed
                          </Badge>
                        )}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <DisciplineGrid
                        group={group}
                        rows={rows}
                        sourceByVisitType={sourceByVisitType}
                        preResetRows={preResetRows}
                        resetChangedCells={resetChangedCells}
                        inheritedChargeType={inheritedChargeType}
                        onUpdateRow={updateRow}
                        onRevertCell={revertResetCell}
                      />
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </>
        )}
      </div>

      {!inResetReview && (
        <div className='flex items-center justify-end gap-2 border-t pt-3'>
          <Button
            variant='outline'
            className='mr-auto'
            onClick={() => void handleResetToDefaults()}
            disabled={isLoadingDefaults || isSaving || !hasLoaded || isDirty}
            title={isDirty ? 'Save or discard your edits first' : undefined}
          >
            {isLoadingDefaults && (
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
            )}
            Reset to defaults
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || !isDirty || !hasLoaded}
          >
            {isSaving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            Save rates
          </Button>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Per-discipline editable grid
// ---------------------------------------------------------------------------

const DECIMAL_RE = /^\d*\.?\d{0,2}$/;
const INT_RE = /^\d*$/;

type DisciplineGridProps = {
  group: DisciplineGroup;
  rows: Record<string, RowState>;
  sourceByVisitType: Record<string, RateSource>;
  preResetRows: Record<string, RowState> | null;
  resetChangedCells: Map<string, ResetField[]>;
  inheritedChargeType: ChargeType | null;
  onUpdateRow: (visitTypeId: string, patch: Partial<RowState>) => void;
  onRevertCell: (visitTypeId: string, field: ResetField) => void;
};

const DisciplineGrid: React.FC<DisciplineGridProps> = ({
  group,
  rows,
  sourceByVisitType,
  preResetRows,
  resetChangedCells,
  inheritedChargeType,
  onUpdateRow,
  onRevertCell,
}) => (
  <div className='overflow-x-auto'>
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className='min-w-[200px]'>Visit type</TableHead>
          <TableHead>Charge by</TableHead>
          <TableHead>Allowed</TableHead>
          <TableHead>Allowed by</TableHead>
          <TableHead>Revenue code</TableHead>
          <TableHead>HCPCS</TableHead>
          <TableHead>Modifiers</TableHead>
          <TableHead>Claim units</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {group.visitTypes.map(vt => (
          <RateRow
            key={vt.visit_type_id}
            visitType={vt}
            row={rows[vt.visit_type_id]}
            source={sourceByVisitType[vt.visit_type_id] ?? null}
            preResetRow={preResetRows?.[vt.visit_type_id] ?? null}
            changedFields={resetChangedCells.get(vt.visit_type_id) ?? []}
            inheritedChargeType={inheritedChargeType}
            onUpdate={patch => onUpdateRow(vt.visit_type_id, patch)}
            onRevertCell={field => onRevertCell(vt.visit_type_id, field)}
          />
        ))}
      </TableBody>
    </Table>
  </div>
);

type RateRowProps = {
  visitType: InsuranceVisitRateVisitType;
  row: RowState;
  source: RateSource | null;
  preResetRow: RowState | null;
  changedFields: readonly ResetField[];
  inheritedChargeType: ChargeType | null;
  onUpdate: (patch: Partial<RowState>) => void;
  onRevertCell: (field: ResetField) => void;
};

// WellSky-synced rows can carry a basis the dropdowns no longer offer for new
// picks (charge/allowed by CONSTANT, decoded from WellSky lookup 15; billing
// charges it flat). Render the current value so the Select isn't blank and a
// save round-trips it instead of silently dropping it.
const legacyBasisItem = (current: ChargeType | '') =>
  current && !RATE_CHARGE_BASIS_OPTIONS.some(o => o.value === current) ? (
    <SelectItem value={current}>{CHARGE_TYPE_LABELS[current]}</SelectItem>
  ) : null;

// A code cell during a defaults review: when the proposal changed it, reveal
// the pre-reset value and a revert action on hover or keyboard focus.
const ResetRevertOverlay: React.FC<{
  label: string;
  previousValue: string;
  onRevert: () => void;
}> = ({ label, previousValue, onRevert }) => (
  // pt-1 (not a margin) keeps the hover chain unbroken between the input and
  // the card, so the cursor can travel down to the Revert button without the
  // overlay disappearing en route.
  <span className='absolute left-0 top-full z-10 hidden w-max pt-1 group-hover:block group-focus-within:block'>
    <span className='flex min-w-max items-center gap-2 rounded-md border border-seeded-border/50 bg-popover px-2 py-1 text-xs shadow-md'>
      <span className='text-muted-foreground'>
        Was {previousValue === '' ? 'blank' : `“${previousValue}”`}
      </span>
      <button
        type='button'
        className='inline-flex items-center gap-1 font-medium text-seeded-foreground hover:underline'
        aria-label={`Revert ${label}`}
        onClick={onRevert}
      >
        <Undo2 className='h-3 w-3' />
        Revert
      </button>
    </span>
  </span>
);
const RateRow: React.FC<RateRowProps> = ({
  visitType,
  row,
  source,
  preResetRow,
  changedFields,
  inheritedChargeType,
  onUpdate,
  onRevertCell,
}) => {
  const modifiers = parseModifiers(row.modifiersText);
  const tooManyModifiers = modifiers.length > MAX_MODIFIERS;
  const isConstant = row.claimUnitsBasis === 'CONSTANT';

  // When the admin picks Constant, the unit-count input appears at the far
  // right of a horizontally scrollable grid — bring it into view and focus it
  // so it can't be missed and left blank. Seeded CONSTANT rows don't trigger
  // this (no scroll/focus stealing on load).
  const constantInputRef = useRef<HTMLInputElement>(null);
  const [revealConstantInput, setRevealConstantInput] = useState(false);
  useEffect(() => {
    if (revealConstantInput && constantInputRef.current) {
      // Optional call: scrollIntoView is missing in jsdom.
      constantInputRef.current.scrollIntoView?.({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
      constantInputRef.current.focus({ preventScroll: true });
      setRevealConstantInput(false);
    }
  }, [revealConstantInput]);

  // The Inherit option resolves to the insurance-level charge type; show it
  // so the admin doesn't have to remember what the insurance is set to.
  const inheritLabel = inheritedChargeType
    ? `Inherit (${CHARGE_TYPE_LABELS[inheritedChargeType]})`
    : 'Inherit';

  const cellPad = 'px-2 py-1.5 align-top';

  // Revenue/HCPCS carry the seeded defaults: gold resting tint while the row
  // is machine-seeded (source DEFAULT), stronger tint plus a revert overlay
  // while a defaults review proposes a change to the cell.
  const renderCodeCell = (
    field: ResetField,
    widthClass: string,
    ariaLabel: string
  ) => {
    const isSeeded = source === 'DEFAULT';
    const isChanged = changedFields.includes(field);
    return (
      <span className='group relative inline-block'>
        <Input
          inputSize='sm'
          className={`${widthClass} ${
            isChanged
              ? 'border-seeded-border bg-seeded-strong'
              : isSeeded
                ? 'border-seeded-border/50 bg-seeded'
                : ''
          }`}
          placeholder='—'
          aria-label={ariaLabel}
          data-seeded={isSeeded || undefined}
          data-reset-changed={isChanged || undefined}
          value={row[field]}
          onChange={e => onUpdate({ [field]: e.target.value })}
        />
        {isChanged && preResetRow && (
          <ResetRevertOverlay
            label={ariaLabel}
            previousValue={preResetRow[field]}
            onRevert={() => onRevertCell(field)}
          />
        )}
      </span>
    );
  };

  return (
    <TableRow>
      {/* No nowrap: the longest OASIS visit-type names wrap to a second line
          rather than pushing the grid past the modal edge. */}
      <TableCell className={cellPad}>
        <span className='flex items-center gap-2'>
          <span className='text-sm'>
            {visitType.display_name}
            <AlsoCalled aliases={visitType.aliases} />
          </span>
          {!row.active && (
            <Badge variant='secondary' className='text-muted-foreground'>
              Inactive
            </Badge>
          )}
        </span>
      </TableCell>

      <TableCell className={cellPad}>
        <Select
          value={row.chargeBy || INHERIT}
          onValueChange={v =>
            onUpdate({ chargeBy: v === INHERIT ? '' : (v as ChargeType) })
          }
        >
          <SelectTrigger
            selectSize='sm'
            className='w-32'
            aria-label={`${visitType.display_name} charge by`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={INHERIT}>{inheritLabel}</SelectItem>
            {RATE_CHARGE_BASIS_OPTIONS.map(o => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
            {legacyBasisItem(row.chargeBy)}
          </SelectContent>
        </Select>
      </TableCell>

      <TableCell className={cellPad}>
        <Input
          inputSize='sm'
          className='w-20'
          inputMode='decimal'
          placeholder='—'
          aria-label={`${visitType.display_name} allowed`}
          value={row.allowedAmount}
          onChange={e => {
            const v = e.target.value;
            if (v === '' || DECIMAL_RE.test(v)) onUpdate({ allowedAmount: v });
          }}
        />
      </TableCell>

      <TableCell className={cellPad}>
        <Select
          value={row.allowedBy || INHERIT}
          onValueChange={v =>
            onUpdate({ allowedBy: v === INHERIT ? '' : (v as ChargeType) })
          }
        >
          <SelectTrigger
            selectSize='sm'
            className='w-32'
            aria-label={`${visitType.display_name} allowed by`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={INHERIT}>{inheritLabel}</SelectItem>
            {RATE_CHARGE_BASIS_OPTIONS.map(o => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
            {legacyBasisItem(row.allowedBy)}
          </SelectContent>
        </Select>
      </TableCell>

      <TableCell className={cellPad}>
        {renderCodeCell(
          'revenueCode',
          'w-20',
          `${visitType.display_name} revenue code`
        )}
      </TableCell>

      <TableCell className={cellPad}>
        {renderCodeCell('hcpcsCode', 'w-20', `${visitType.display_name} HCPCS`)}
      </TableCell>

      <TableCell className={cellPad}>
        <Input
          inputSize='sm'
          className='w-24'
          placeholder='e.g. GT KX'
          aria-label={`${visitType.display_name} modifiers`}
          aria-invalid={tooManyModifiers}
          value={row.modifiersText}
          onChange={e => onUpdate({ modifiersText: e.target.value })}
        />
        {tooManyModifiers && (
          <p className='mt-1 text-xs text-destructive'>Max {MAX_MODIFIERS}</p>
        )}
      </TableCell>

      <TableCell className={cellPad}>
        <div className='flex items-center gap-1.5'>
          <Select
            value={row.claimUnitsBasis || INHERIT}
            onValueChange={v => {
              const next = v === INHERIT ? '' : (v as ChargeType);
              onUpdate({
                claimUnitsBasis: next,
                // Constant only makes sense for CONSTANT; clear it otherwise.
                ...(next === 'CONSTANT' ? {} : { claimUnitsConstant: '' }),
              });
              if (next === 'CONSTANT') setRevealConstantInput(true);
            }}
          >
            <SelectTrigger
              selectSize='sm'
              className='w-32'
              aria-label={`${visitType.display_name} claim units`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={INHERIT}>{inheritLabel}</SelectItem>
              {CLAIM_UNITS_BASIS_OPTIONS.map(o => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isConstant && (
            <Input
              ref={constantInputRef}
              inputSize='sm'
              className='w-16'
              inputMode='numeric'
              placeholder='#'
              aria-label={`${visitType.display_name} constant units`}
              value={row.claimUnitsConstant}
              onChange={e => {
                const v = e.target.value;
                if (v === '' || INT_RE.test(v))
                  onUpdate({ claimUnitsConstant: v });
              }}
            />
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

export default InsuranceRatesPanel;
